import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';

export type ConfirmationType = 'warning' | 'danger' | 'info' | 'success';

interface ConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  description: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmationType;
  children?: ReactNode;
}

const typeConfig = {
  warning: {
    icon: AlertTriangle,
    iconClassName: 'h-12 w-12 text-yellow-600 dark:text-yellow-500',
    containerClassName: 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-900',
    confirmClassName: 'bg-yellow-600 hover:bg-yellow-700 text-white',
  },
  danger: {
    icon: XCircle,
    iconClassName: 'h-12 w-12 text-red-600 dark:text-red-500',
    containerClassName: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900',
    confirmClassName: 'bg-red-600 hover:bg-red-700 text-white',
  },
  info: {
    icon: Info,
    iconClassName: 'h-12 w-12 text-blue-600 dark:text-blue-500',
    containerClassName: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900',
    confirmClassName: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  success: {
    icon: CheckCircle2,
    iconClassName: 'h-12 w-12 text-green-600 dark:text-green-500',
    containerClassName: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900',
    confirmClassName: 'bg-green-600 hover:bg-green-700 text-white',
  },
};

export function ConfirmationModal({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  children,
}: ConfirmationModalProps): React.ReactElement {
  const config = typeConfig[type];
  const Icon = config.icon;

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-start gap-4">
            <div className={`rounded-full p-3 border-2 ${config.containerClassName}`}>
              <Icon className={config.iconClassName} />
            </div>
            <div className="flex-1 pt-1">
              <AlertDialogTitle className="text-xl mb-2">{title}</AlertDialogTitle>
              {typeof description === 'string' ? (
                <AlertDialogDescription className="text-base">
                  {description}
                </AlertDialogDescription>
              ) : (
                <AlertDialogDescription asChild className="text-base">
                  <div className="text-muted-foreground">{description}</div>
                </AlertDialogDescription>
              )}
            </div>
          </div>
        </AlertDialogHeader>
        {children && <div className="py-4">{children}</div>}
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelText}</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} className={config.confirmClassName}>
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
